import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, NotebookPen, Smile, Meh, Frown, X, Loader2 } from "lucide-react";
import InsightsCard from "@/components/InsightsCard";
import { useProfile } from "@/context/ProfileContext";

const MOODS = [
  { v: "great", label: "Great", color: "#16A34A" },
  { v: "good", label: "Good", color: "#22C55E" },
  { v: "okay", label: "Okay", color: "#D97706" },
  { v: "rough", label: "Rough", color: "#EA580C" },
  { v: "terrible", label: "Terrible", color: "#DC2626" },
];

const COMMON_SYMPTOMS = [
  "Headache", "Nausea", "Dizziness", "Fatigue", "Insomnia", "Drowsiness",
  "Stomach ache", "Diarrhea", "Constipation", "Anxiety", "Irritability",
  "Dry mouth", "Sweating", "Rash", "Muscle aches", "Heart palpitations",
  "Blurred vision", "Loss of appetite", "Weight change", "Brain fog",
];

function todayISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().split("T")[0];
}

export default function Journal() {
  const { activeId } = useProfile();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [date, setDate] = useState(todayISO());
  const [mood, setMood] = useState("okay");
  const [severity, setSeverity] = useState([3]);
  const [symptomInput, setSymptomInput] = useState("");
  const [symptoms, setSymptoms] = useState([]);
  const [notes, setNotes] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/journal");
      setEntries(data);
    } catch (_e) {
      toast.error("Could not load journal.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!activeId) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const addSymptom = (s) => {
    const clean = s.trim();
    if (!clean) return;
    if (symptoms.some((x) => x.toLowerCase() === clean.toLowerCase())) return;
    setSymptoms([...symptoms, clean]);
    setSymptomInput("");
  };
  const removeSymptom = (s) => setSymptoms(symptoms.filter((x) => x !== s));

  const save = async () => {
    if (!date) return toast.error("Please pick a date.");
    setSaving(true);
    try {
      const { data } = await api.post("/journal", {
        date,
        mood,
        symptoms,
        severity: severity[0],
        notes,
      });
      setEntries([data, ...entries]);
      setMood("okay");
      setSeverity([3]);
      setSymptoms([]);
      setNotes("");
      toast.success("Entry saved.");
    } catch (_e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.delete(`/journal/${id}`);
      setEntries(entries.filter((e) => e.id !== id));
    } catch (_e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 fade-up">
      <div className="mb-8 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: "#E8F0EC" }}>
          <NotebookPen size={20} style={{ color: "#2D5A4C" }} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#5C6B66" }}>
            Symptom Journal
          </p>
          <h1 className="font-serif-display text-3xl sm:text-4xl font-semibold" style={{ color: "#1C2826" }}>
            How are you feeling today?
          </h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* NEW ENTRY */}
        <div className="lg:col-span-2 card-warm p-6 h-fit sticky top-32">
          <h2 className="font-serif-display text-2xl font-medium mb-4" style={{ color: "#1C2826" }}>
            New entry
          </h2>

          <div className="space-y-4">
            <div>
              <Label>Date</Label>
              <Input
                data-testid="journal-date-input"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-1.5 h-11 rounded-xl"
              />
            </div>

            <div>
              <Label>Overall mood</Label>
              <Select value={mood} onValueChange={setMood}>
                <SelectTrigger data-testid="journal-mood-trigger" className="mt-1.5 h-11 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOODS.map((m) => (
                    <SelectItem key={m.v} value={m.v}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                        {m.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Severity</Label>
                <span className="text-xs font-semibold" style={{ color: "#2D5A4C" }} data-testid="journal-severity-value">
                  {severity[0]}/10
                </span>
              </div>
              <Slider
                data-testid="journal-severity-slider"
                value={severity}
                onValueChange={setSeverity}
                min={0}
                max={10}
                step={1}
                className="mt-3"
              />
            </div>

            <div>
              <Label>Symptoms</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  data-testid="journal-symptom-input"
                  value={symptomInput}
                  onChange={(e) => setSymptomInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addSymptom(symptomInput);
                    }
                  }}
                  placeholder="Add symptom + Enter"
                  className="h-11 rounded-xl"
                />
                <Button
                  data-testid="journal-add-symptom-btn"
                  onClick={() => addSymptom(symptomInput)}
                  className="btn-brand rounded-full h-11 px-4"
                >
                  <Plus size={16} />
                </Button>
              </div>

              {symptoms.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3" data-testid="journal-symptom-tags">
                  {symptoms.map((s) => (
                    <Badge
                      key={s}
                      className="rounded-full text-xs pl-2 pr-1 py-1 flex items-center gap-1"
                      style={{ background: "#E8F0EC", color: "#23473C", border: "none" }}
                    >
                      {s}
                      <button
                        onClick={() => removeSymptom(s)}
                        className="p-0.5 rounded-full hover:bg-white/50"
                        aria-label={`Remove ${s}`}
                      >
                        <X size={12} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap gap-1.5 mt-3">
                {COMMON_SYMPTOMS.slice(0, 10).map((s) => (
                  <button
                    key={s}
                    data-testid={`journal-suggest-${s.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => addSymptom(s)}
                    disabled={symptoms.includes(s)}
                    className="text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-40"
                    style={{ borderColor: "#E5DEC9", color: "#5C6B66", background: "#FFFFFF" }}
                  >
                    + {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                data-testid="journal-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1.5 rounded-xl"
                placeholder="Anything else worth noting?"
              />
            </div>

            <Button
              data-testid="journal-save-btn"
              onClick={save}
              disabled={saving}
              className="btn-brand w-full h-12 rounded-full text-sm font-semibold"
            >
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Save entry
            </Button>
          </div>
        </div>

        {/* TIMELINE */}
        <div className="lg:col-span-3 space-y-4">
          <InsightsCard />
          <h2 className="font-serif-display text-2xl font-medium" style={{ color: "#1C2826" }}>
            Your timeline
          </h2>

          {loading ? (
            <div className="text-sm" style={{ color: "#5C6B66" }}>Loading…</div>
          ) : entries.length === 0 ? (
            <div className="card-warm p-8 text-center">
              <Meh size={28} className="mx-auto mb-2" style={{ color: "#5C6B66" }} />
              <p className="text-sm" style={{ color: "#5C6B66" }}>
                No entries yet. Save your first one to start noticing patterns.
              </p>
            </div>
          ) : (
            <ul className="space-y-3" data-testid="journal-entries-list">
              {entries.map((e) => {
                const moodMeta = MOODS.find((m) => m.v === e.mood) || MOODS[2];
                const Icon = ["great", "good"].includes(e.mood) ? Smile : e.mood === "okay" ? Meh : Frown;
                return (
                  <li key={e.id} className="card-warm p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-xl grid place-items-center shrink-0"
                          style={{ background: `${moodMeta.color}22` }}
                        >
                          <Icon size={18} style={{ color: moodMeta.color }} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                            {new Date(e.date + "T00:00:00").toLocaleDateString(undefined, {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                          <p className="text-xs" style={{ color: "#5C6B66" }}>
                            {moodMeta.label} · Severity {e.severity}/10
                          </p>
                        </div>
                      </div>
                      <button
                        data-testid={`journal-delete-${e.id}`}
                        onClick={() => del(e.id)}
                        className="p-2 rounded-full hover:bg-[#FDF0EA] transition-colors"
                        aria-label="Delete entry"
                      >
                        <Trash2 size={15} style={{ color: "#BF5832" }} />
                      </button>
                    </div>
                    {e.symptoms?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {e.symptoms.map((s) => (
                          <Badge
                            key={s}
                            className="rounded-full text-xs"
                            style={{ background: "#F4EFE6", color: "#1C2826", border: "1px solid #E5DEC9" }}
                          >
                            {s}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {e.notes && (
                      <p className="mt-3 text-sm italic" style={{ color: "#5C6B66" }}>
                        "{e.notes}"
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
